import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Scale,
  FileText,
  AlertTriangle,
  CreditCard,
  Shield,
  Gavel,
  XCircle,
  Mail
} from 'lucide-react';
import PageLayout, { PageHero } from './layout/PageLayout';
import SEO from './SEO';

const TermsPage = ({ onBack, onLogin, onRegister }) => {
  const lastUpdated = 'February 15, 2024';

  const sections = [
    {
      id: 'acceptance',
      icon: FileText,
      title: 'Acceptance of Terms',
      content: `By accessing or using Lexa.run ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.

These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years of age and have the legal capacity to enter into these Terms.`
    },
    {
      id: 'description',
      icon: Scale,
      title: 'Description of Service',
      content: `Lexa.run provides an AI-powered test automation platform that allows users to:
      
• Generate automated tests from plain English descriptions
• Create and manage test flows, API tests, and test suites
• Execute tests and view results
• Manage QA lifecycle including requirements and test scenarios
• Schedule automated test runs
• Collaborate with team members

We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time with or without notice.`
    },
    {
      id: 'accounts',
      icon: Shield,
      title: 'User Accounts',
      content: `When you create an account, you must provide accurate and complete information. You are responsible for:

• Maintaining the confidentiality of your account credentials
• All activities that occur under your account
• Notifying us immediately of any unauthorized access

We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent, abusive, or illegal activity.`
    },
    {
      id: 'payment',
      icon: CreditCard,
      title: 'Payment Terms',
      content: `For paid subscription plans:

• Fees are billed in advance on a monthly or annual basis
• All fees are non-refundable except as required by law or as stated in our refund policy
• You authorize us to charge your payment method for all fees incurred
• We may change pricing with 30 days' notice
• Failure to pay may result in suspension or termination of your account

Free trial accounts are subject to usage limits. Exceeding limits may require upgrading to a paid plan.`
    },
    {
      id: 'acceptable-use',
      icon: AlertTriangle,
      title: 'Acceptable Use Policy',
      content: `You agree NOT to use the Service to:

• Violate any applicable laws or regulations
• Infringe on intellectual property rights of others
• Transmit malware, viruses, or harmful code
• Attempt to gain unauthorized access to our systems
• Use the Service for illegal testing or hacking activities
• Abuse, harass, or harm other users
• Scrape or collect data without permission
• Resell or redistribute the Service without authorization

We may investigate violations and take appropriate action, including removing content, suspending accounts, or involving law enforcement.`
    },
    {
      id: 'intellectual-property',
      icon: Scale,
      title: 'Intellectual Property',
      content: `The Service and its original content, features, and functionality are owned by Lexa.run and are protected by international copyright, trademark, and other intellectual property laws.

You retain ownership of your content (tests, configurations, data). By using the Service, you grant us a license to use, store, and process your content solely to provide the Service.

AI-generated tests are provided for your use but may incorporate our proprietary methods and algorithms.`
    },
    {
      id: 'limitation',
      icon: XCircle,
      title: 'Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

• The Service is provided "AS IS" without warranties of any kind
• We do not guarantee uninterrupted, secure, or error-free operation
• We are not liable for any indirect, incidental, special, or consequential damages
• Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim

You acknowledge that automated testing involves inherent risks and that test results should be validated before production use.`
    },
    {
      id: 'termination',
      icon: Gavel,
      title: 'Termination',
      content: `We may terminate or suspend your account immediately, without prior notice, for:

• Violation of these Terms
• Non-payment of fees
• Fraudulent or illegal activity
• At our sole discretion for any reason

Upon termination:
• Your right to use the Service will immediately cease
• You may request export of your data within 30 days
• We may delete your data after a reasonable retention period`
    },
    {
      id: 'governing-law',
      icon: Scale,
      title: 'Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.

Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.`
    },
    {
      id: 'changes',
      icon: FileText,
      title: 'Changes to Terms',
      content: `We reserve the right to modify these Terms at any time. We will notify users of material changes by:

• Posting a notice on our website
• Sending an email to registered users
• Updating the "Last Updated" date

Your continued use of the Service after changes constitutes acceptance of the new Terms.`
    }
  ];

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Terms of Service - Lexa.run"
        description="Terms of Service for Lexa.run's AI-powered test automation platform. Read our legal terms, user responsibilities, and service conditions."
      />

      <PageHero
        badge={<><Scale className="w-4 h-4" /> Terms of Service</>}
        title="Terms of"
        titleHighlight="Service"
        subtitle="Please read these terms carefully before using Lexa.run. By using our service, you agree to be bound by these terms."
      />

      {/* Last Updated */}
      <section className="py-8 -mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <FileText className="w-4 h-4" />
            Last updated: {lastUpdated}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-6 rounded-2xl border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Contents</h3>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors py-1"
                >
                  <section.icon className="w-4 h-4" />
                  {section.title}
                </a>
              ))}
            </nav>
          </Card>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-32">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-purple-100">
                  <section.icon className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              <div className="pl-16">
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 text-white mb-6">
            <Mail className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions about our terms?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            If you have any questions about these Terms of Service, 
            please contact our legal team.
          </p>
          <a
            href="mailto:legal@lexa.run"
            className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors"
          >
            <Mail className="w-5 h-5" />
            legal@lexa.run
          </a>
        </div>
      </section>
    </PageLayout>
  );
};

export default TermsPage;
